import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { Menu, ClipboardList, Server, Link2, Activity, Database, BarChart2, Rss, TrendingUp, Radio } from 'lucide-react';
import { useUser }      from '../../context/UserContext';
import { usePrices }    from '../../context/PriceContext';
import { useSidePanel } from '../../context/SidePanelContext';
import { useToday }     from '../../context/TodayContext';
import { getHealth, apiPost, getDataStatus, syncEodData, recomputeRS, completeCalendarItem, reopenCalendarItem } from '../../api/client';
import { getDeskStatus } from '../../api/desk';
import { INDEX_REGISTRY } from '../../data/indices';
import logoLockup from '../../assets/logo-lockup-light.svg';
import logoMark   from '../../assets/logo-mark.svg';

// ── Global search universes (built once at module load) ────────────────────

const _SEARCH_INDICES = INDEX_REGISTRY
  .filter(e => ['Index','Broad','Sector','Thematic'].includes(e.category))
  .map(e => ({ display: e.label, sym: e.kiteSymbol }));

const _SEARCH_ASSETS = [
  ...INDEX_REGISTRY
    .filter(e => ['Commodity','Currency','Crypto','Debt'].includes(e.category))
    .map(e => ({ display: e.label, sym: `YF:${e.kiteSymbol}` })),
  { display:'Nikkei 225',  sym:'YF:^N225'   },
  { display:'Hang Seng',   sym:'YF:^HSI'    },
  { display:'DAX',         sym:'YF:^GDAXI'  },
  { display:'Dow Jones',   sym:'YF:^DJI'    },
  { display:'S&P 500',     sym:'YF:^GSPC'   },
  { display:'Nasdaq',      sym:'YF:^IXIC'   },
];

const _TOPBAR_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Search helpers ───────────────────────────────────────────────────────────
const HISTORY_KEY = 'sp_search_history';
const getHistory    = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; } };
const addToHistory  = sym => { try { const h = [sym, ...getHistory().filter(s => s !== sym)].slice(0, 10); localStorage.setItem(HISTORY_KEY, JSON.stringify(h)); } catch {} };
const clearHistory  = () => { try { localStorage.removeItem(HISTORY_KEY); } catch {} };

// Fuzzy scorer: 0=no match, 0.5=chars in order, 1=contains, 2=starts-with, 3=exact
function fuzzyScore(haystack, needle) {
  if (!haystack || !needle) return 0;
  if (haystack === needle) return 3;
  if (haystack.startsWith(needle)) return 2;
  if (haystack.includes(needle)) return 1;
  let hi = 0;
  for (let ni = 0; ni < needle.length; ni++) {
    const pos = haystack.indexOf(needle[ni], hi);
    if (pos === -1) return 0;
    hi = pos + 1;
  }
  return 0.5;
}

// ── Global search bar ───────────────────────────────────────────────────────
function GlobalSearchBar() {
  const { open: openPanel } = useSidePanel();
  const [active,      setActive]      = useState(false);
  const [query,       setQuery]       = useState('');
  const [stocks,      setStocks]      = useState([]);  // [{sym, name}]
  const [showDrop,    setShowDrop]    = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [dropPos,     setDropPos]     = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);

  useEffect(() => {
    fetch(`${_TOPBAR_API}/api/journal/symbol-universe`)
      .then(r => r.json())
      .then(d => setStocks(d.symbols || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDrop(false);
        setShowHistory(false);
        setActive(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useLayoutEffect(() => {
    if ((showDrop || showHistory) && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left });
    }
  }, [showDrop, showHistory, active, query]);

  const groups = useMemo(() => {
    if (!query || query.length < 1) return null;
    const q = query.toUpperCase();
    const rankFuzzy = (list, getTexts) =>
      list
        .map(x => ({ x, score: Math.max(...getTexts(x).map(t => fuzzyScore(t.toUpperCase(), q))) }))
        .filter(r => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(r => r.x);

    const assets  = rankFuzzy(_SEARCH_ASSETS,  x => [x.display]).slice(0, 6);
    const indices = rankFuzzy(_SEARCH_INDICES, x => [x.display]).slice(0, 6);
    const stk     = rankFuzzy(stocks,          x => [x.sym, x.name || '']).slice(0, 8);
    if (!assets.length && !indices.length && !stk.length) return null;
    return { assets, indices, stocks: stk };
  }, [query, stocks]);

  const handleSelect = sym => {
    addToHistory(sym);
    openPanel({ name: sym });
    setQuery('');
    setShowDrop(false);
    setShowHistory(false);
    setActive(false);
  };

  const activate = () => {
    setActive(true);
    const h = getHistory();
    setHistoryList(h);
    if (h.length > 0) setShowHistory(true);
    setTimeout(() => inputRef.current?.focus(), 40);
  };

  const rowStyle = {
    padding:'5px 12px', cursor:'pointer', fontSize:11, fontWeight:600,
    color:'var(--ink)', fontFamily:'var(--font-ui)', letterSpacing:'.02em',
    display:'flex', alignItems:'center', gap:6,
  };

  const groupHeader = label => (
    <div style={{
      padding:'5px 12px 3px', fontSize:11, fontWeight:700, letterSpacing:'var(--track-caps)',
      color:'var(--ink-3)', textTransform:'uppercase',
      borderTop:'0.5px solid var(--hair)',
    }}>{label}</div>
  );

  const dropStyle = {
    position:'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999,
    background:'var(--surface)', border:'0.5px solid var(--hair)',
    borderRadius:'var(--r-3)', boxShadow:'var(--shadow-panel)',
    minWidth:260, maxHeight:340, overflowY:'auto',
  };

  return (
    <div ref={wrapRef} style={{ position:'relative', display:'flex', alignItems:'center' }}>
      {active ? (
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="var(--ink-3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink:0 }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => {
              const v = e.target.value.toUpperCase();
              setQuery(v);
              if (v) { setShowDrop(true); setShowHistory(false); }
              else   { setShowDrop(false); setHistoryList(getHistory()); setShowHistory(getHistory().length > 0); }
            }}
            onFocus={() => { if (!query) { const h = getHistory(); setHistoryList(h); if (h.length > 0) setShowHistory(true); } }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setActive(false); setQuery(''); setShowDrop(false); setShowHistory(false); }
              if (e.key === 'Enter') {
                const first = groups?.assets[0] || groups?.indices[0];
                if (first) { handleSelect(first.sym); return; }
                if (groups?.stocks[0]) { handleSelect(groups.stocks[0].sym); return; }
                if (showHistory && historyList[0]) handleSelect(historyList[0]);
              }
            }}
            placeholder="Search stocks, indices, assets…"
            autoComplete="off"
            style={{
              background:'var(--surface-2)', border:'0.5px solid var(--hair)',
              borderRadius:'var(--r-2)', color:'var(--ink)', fontSize:11,
              padding:'3px 10px', width:220, outline:'none',
              fontFamily:'var(--font-ui)',
            }}
          />
          <button
            onClick={() => { setActive(false); setQuery(''); setShowDrop(false); setShowHistory(false); }}
            title="Close"
            style={{
              background:'transparent', border:'none', cursor:'pointer',
              color:'var(--ink-3)', fontSize:14, lineHeight:1, padding:'0 2px',
            }}
          >✕</button>
        </div>
      ) : (
        <button
          onClick={activate}
          title="Search stocks, indices and assets"
          style={{
            background:'transparent', border:'none',
            cursor:'pointer', color:'var(--ink-3)',
            padding:'4px', display:'flex', alignItems:'center',
            borderRadius:'var(--r-1)',
          }}
          onMouseEnter={e => e.currentTarget.style.color='var(--ink-2)'}
          onMouseLeave={e => e.currentTarget.style.color='var(--ink-3)'}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </button>
      )}

      {/* History dropdown — shown when active with no query */}
      {showHistory && !query && historyList.length > 0 && (
        <div style={dropStyle}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'5px 12px 3px' }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:'var(--track-caps)', color:'var(--ink-3)', textTransform:'uppercase' }}>Recent</span>
            <span
              onMouseDown={e => { e.preventDefault(); clearHistory(); setHistoryList([]); setShowHistory(false); }}
              style={{ fontSize:10, color:'var(--ink-3)', cursor:'pointer', opacity:.7 }}
            >Clear</span>
          </div>
          {historyList.map(sym => (
            <div key={sym}
              onMouseDown={e => { e.preventDefault(); handleSelect(sym); }}
              style={rowStyle}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <span style={{fontSize:11,color:'var(--ink-3)',fontWeight:400,minWidth:36,letterSpacing:'.04em'}}>NSE</span>
              {sym}
            </div>
          ))}
        </div>
      )}

      {/* Query results dropdown */}
      {showDrop && groups && (
        <div style={dropStyle}>
          {groups.assets.length > 0 && (<>
            {groupHeader('Assets')}
            {groups.assets.map(item => (
              <div key={item.sym}
                onMouseDown={e => { e.preventDefault(); handleSelect(item.sym); }}
                style={rowStyle}
                onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <span style={{fontSize:11,color:'var(--ink-3)',fontWeight:400,minWidth:36,letterSpacing:'.04em'}}>ASSET</span>
                {item.display}
              </div>
            ))}
          </>)}
          {groups.indices.length > 0 && (<>
            {groupHeader('Indices')}
            {groups.indices.map(item => (
              <div key={item.sym}
                onMouseDown={e => { e.preventDefault(); handleSelect(item.sym); }}
                style={rowStyle}
                onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <span style={{fontSize:11,color:'var(--ink-3)',fontWeight:400,minWidth:36,letterSpacing:'.04em'}}>IDX</span>
                {item.display}
              </div>
            ))}
          </>)}
          {groups.stocks.length > 0 && (<>
            {groupHeader('Stocks')}
            {groups.stocks.map(({sym, name}) => (
              <div key={sym}
                onMouseDown={e => { e.preventDefault(); handleSelect(sym); }}
                style={rowStyle}
                onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <span style={{fontSize:11,color:'var(--ink-3)',fontWeight:400,minWidth:36,letterSpacing:'.04em'}}>NSE</span>
                {sym}
                {name && <span style={{fontSize:10,color:'var(--ink-3)',fontWeight:400,marginLeft:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}}>{name}</span>}
              </div>
            ))}
          </>)}
        </div>
      )}
    </div>
  );
}

// ── Clock + date — two lines, seconds ──────────────────────────────────────
function ClockDate() {
  const [time, setTime] = useState({ hms: '', daydate: '' });

  useEffect(() => {
    const tick = () => {
      const ist  = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const h    = String(ist.getHours()).padStart(2, '0');
      const m    = String(ist.getMinutes()).padStart(2, '0');
      const s    = String(ist.getSeconds()).padStart(2, '0');
      const day  = ist.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase();
      const date = ist.getDate();
      const mon  = ist.toLocaleDateString('en-US', { month: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase();
      setTime({ hms: `${h}:${m}:${s}`, daydate: `${day} ${date} ${mon}` });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
      <span style={{
        fontSize: 13, fontWeight: 600, color: 'var(--ink)',
        fontFamily: 'var(--font-data)', letterSpacing: '.02em',
      }}>
        {time.hms} <span style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400 }}>IST</span>
      </span>
      <span style={{
        fontSize: 10, color: 'var(--ink-3)',
        fontFamily: 'var(--font-data)', letterSpacing: '.04em',
      }}>
        {time.daydate}
      </span>
    </div>
  );
}

// ── Source chip for action items ──────────────────────────────────────────
const SOURCE_STYLE = {
  MACRO:     { bg: 'var(--surface-2)',     color: 'var(--ink-2)'    },
  STRATEGY:  { bg: 'var(--up-field-bg)',   color: 'var(--up-field-ink)' },
  PORTFOLIO: { bg: 'var(--amber-bg)',      color: 'var(--amber-ink)' },
  ACTIONS:   { bg: 'rgba(245,158,11,.10)', color: 'var(--amber-ink)' },
  // Infrastructure alarms (data-volume space). Red, not amber: unlike every
  // other source here this one is not a thing to consider today, it is a
  // deadline after which writes start failing.
  SYSTEM:    { bg: 'var(--down-field-bg)', color: 'var(--down-field-ink)' },
  CUSTOM:    { bg: 'var(--surface-2)',     color: 'var(--ink-3)'    },
};
function SourceChip({ source }) {
  const s = SOURCE_STYLE[source] || SOURCE_STYLE.CUSTOM;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
      padding: '1px 5px', borderRadius: 'var(--r-1)', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {source}
    </span>
  );
}

// ── Single action item row ─────────────────────────────────────────────────
function ActionItemRow({ item, onToggleAction }) {
  const done      = !!item.completed_at;
  const isAction  = item.source === 'ACTIONS';
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '5px 0',
      opacity: done ? 0.6 : 1,
    }}>
      {/* Checkbox only for ACTIONS — events stay all day, no tick-off */}
      {isAction ? (
        <input
          type="checkbox"
          checked={done}
          onChange={() => onToggleAction(item.id, done)}
          style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--amber-ink)' }}
        />
      ) : (
        <span style={{ width: 13, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
          <SourceChip source={item.source} />
          <span style={{ fontSize: 10, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{item.time}</span>
          {item.urgent && !done && <span style={{ color: 'var(--amber-ink)', fontSize: 11 }}>⚡</span>}
          {item.overdue && !done && (
            <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--amber-ink)',
              background: 'rgba(245,158,11,.12)', padding: '1px 4px', borderRadius: 2 }}>
              OVERDUE
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-1)', fontWeight: 500, wordBreak: 'break-word',
          textDecoration: done && isAction ? 'line-through' : 'none' }}>
          {item.title}
        </div>
        {item.description && (isAction || item.source === 'SYSTEM') && (
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>{item.description}</div>
        )}
      </div>
    </div>
  );
}

// ── Date label helper ─────────────────────────────────────────────────────
function dateSectionLabel(isoDate) {
  if (!isoDate) return '';
  try {
    const today    = new Date(); today.setHours(0,0,0,0);
    const d        = new Date(isoDate + 'T00:00:00');
    const diffDays = Math.round((d - today) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return isoDate; }
}

// ── Today's Actions panel content (single list, date-grouped) ─────────────
function TodayPanel({ onClose }) {
  const { allItems, refresh } = useToday();
  const [hideCompleted, setHideCompleted] = useState(false);

  const handleToggleAction = async (itemId, isDone) => {
    const rawId = parseInt(itemId.replace('action-', ''), 10);
    if (isNaN(rawId)) return;
    try {
      if (isDone) await reopenCalendarItem(rawId);
      else        await completeCalendarItem(rawId);
      await refresh();
    } catch (e) { /* silent */ }
  };

  const actionItems    = allItems.filter(i => i.source === 'ACTIONS');
  const completedCount = actionItems.filter(i => !!i.completed_at).length;

  const visibleItems = hideCompleted
    ? allItems.filter(i => i.source !== 'ACTIONS' || !i.completed_at)
    : allItems;

  const groups = [];
  let lastDate = null;
  visibleItems.forEach(item => {
    if (item.due_date !== lastDate) {
      groups.push({ date: item.due_date, label: dateSectionLabel(item.due_date), items: [] });
      lastDate = item.due_date;
    }
    groups[groups.length - 1].items.push(item);
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
          textTransform: 'uppercase', color: 'var(--ink-3)',
        }}>
          Events &amp; Actions
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {completedCount > 0 && (
            <button
              onClick={() => setHideCompleted(h => !h)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 10, fontWeight: 600, color: 'var(--ink-3)', padding: 0 }}>
              {hideCompleted ? `Show done (${completedCount})` : `Hide done (${completedCount})`}
            </button>
          )}
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--ink-3)', fontSize: 14, lineHeight: 1, padding: '0 2px',
          }}>✕</button>
        </div>
      </div>
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {!visibleItems.length ? (
          <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', padding: '8px 0' }}>
            {hideCompleted && completedCount > 0 ? 'All done! Click "Show done" to review.' : 'No items in the next 7 days.'}
          </div>
        ) : (
          groups.map((group, gi) => (
            <div key={group.date}>
              {/* Day separator — only between day groups, not between items */}
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                color: 'var(--ink-3)', padding: '8px 0 3px',
                borderTop: gi === 0 ? 'none' : '.5px solid var(--hair)',
                marginTop: gi === 0 ? 0 : 4,
              }}>
                {group.label}
              </div>
              {group.items.map(item => (
                <ActionItemRow
                  key={item.id}
                  item={item}
                  onToggleAction={handleToggleAction}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Today's Actions button — desktop dropdown / mobile bottom sheet ───────
function TodayActions() {
  const { count, urgentCount } = useToday();
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 720;

  useLayoutEffect(() => {
    if (open && !isMobile && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || isMobile) return;
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, isMobile]);

  const sheetRef    = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = e => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd   = e => {
    if (touchStartY.current !== null) {
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      if (dy > 60) setOpen(false);
      touchStartY.current = null;
    }
  };

  const badge = (
    <button
      onClick={() => setOpen(o => !o)}
      className="topbar-today"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 10px',
        background: open ? 'var(--amber-bg)' : 'var(--surface)',
        border: `0.5px solid ${open ? 'var(--amber-hair)' : 'var(--hair-strong)'}`,
        borderRadius: 'var(--r-2)', cursor: 'pointer',
        color: open ? 'var(--amber-ink)' : 'var(--ink-2)',
        fontSize: 11, fontFamily: 'var(--font-ui)', fontWeight: 600,
        transition: 'background .1s, color .1s',
      }}
    >
      <span className="today-label" style={{ letterSpacing: '.01em' }}>Today's Actions</span>
      <ClipboardList size={13} strokeWidth={1.75} className="today-icon" style={{ display: 'none', flexShrink: 0 }} />
      <span style={{
        background: urgentCount > 0 ? 'var(--amber-ink)' : 'var(--surface-2)',
        color: urgentCount > 0 ? '#fff' : 'var(--ink-2)',
        fontSize: 10, fontWeight: 700, lineHeight: 1,
        padding: '2px 6px', borderRadius: 99,
        minWidth: 18, textAlign: 'center',
      }}>
        {count}
      </span>
    </button>
  );

  if (!isMobile) {
    return (
      <div ref={ref}>
        {badge}
        {open && (
          <div style={{
            position: 'fixed', top: pos.top, right: pos.right,
            zIndex: 9998,
            background: 'var(--surface)',
            border: '0.5px solid var(--hair)',
            borderRadius: 'var(--r-3)', boxShadow: 'var(--shadow-panel)',
            width: 340, padding: '12px 14px',
          }}>
            <TodayPanel onClose={() => setOpen(false)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref}>
      {badge}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9997, background: 'rgba(0,0,0,0.35)' }}
          />
          <div
            ref={sheetRef}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{
              position: 'fixed', bottom: 0, left: 0, right: 0,
              zIndex: 9998, height: '70vh',
              background: 'var(--surface)',
              borderRadius: '14px 14px 0 0',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
              display: 'flex', flexDirection: 'column',
              animation: 'slideUp .25s ease-out',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--hair-strong)' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 24px' }}>
              <TodayPanel onClose={() => setOpen(false)} />
            </div>
          </div>
          <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        </>
      )}
    </div>
  );
}

// ── Status chip — redesigned ───────────────────────────────────────────────
// Healthy: green dot only. Degraded: amber pill + worst-issue label + inline
// action + +N extra count + caret. Panel: structured CONNECTION / DATA FRESHNESS.
function StatusChip() {
  const { kiteStatus, indicesRaw, lastFetched, indicesStale, refresh: refreshPrices } = usePrices();
  const { isOwner } = useUser();
  const [backendUp,     setBackendUp]     = useState(true);
  const [backendSeen,   setBackendSeen]   = useState(null); // last-seen timestamp when offline
  const [dataStatus,    setDataStatus]    = useState(null);
  const [deskState,     setDeskState]     = useState(null); // 'TICKER_DOWN' | 'TOKEN_EXPIRED' | others
  const [open,          setOpen]          = useState(false);
  const [pos,           setPos]           = useState({ top: 0, right: 0 });
  const [refreshing,    setRefreshing]    = useState(false);
  const [syncBusy,      setSyncBusy]      = useState(false);
  const [recomputeBusy, setRecomputeBusy] = useState(false);
  const [panelSec,      setPanelSec]      = useState(''); // "as of HH:MM:SS" ticking in panel
  const ref = useRef(null);

  const KITE_LOGIN_URL = `${BACKEND_URL}/kite/login`;

  // Backend health poll (30s)
  useEffect(() => {
    const check = () =>
      getHealth()
        .then(r => {
          const up = !r._error;
          setBackendUp(up);
          if (up) setBackendSeen(null);
        })
        .catch(() => setBackendUp(false));
    check();
    const iv = setInterval(check, 30_000);
    return () => clearInterval(iv);
  }, []);

  // Data status poll (5 min)
  useEffect(() => {
    const load = () =>
      getDataStatus().then(r => { if (!r?._error) setDataStatus(r); }).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60_000);
    return () => clearInterval(id);
  }, []);

  // Desk status poll (30 s) — surfaces TICKER_DOWN / TOKEN_EXPIRED
  useEffect(() => {
    const load = () =>
      getDeskStatus().then(r => { if (!r?._error) setDeskState(r?.state || null); }).catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useLayoutEffect(() => {
    if (open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
  }, [open]);

  // "as of HH:MM:SS" seconds ticker for panel header
  useEffect(() => {
    if (!open) return;
    const tick = () => {
      const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const h   = String(ist.getHours()).padStart(2, '0');
      const m   = String(ist.getMinutes()).padStart(2, '0');
      const s   = String(ist.getSeconds()).padStart(2, '0');
      setPanelSec(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open]);

  // ── Derive issues list (ordered worst → best) ──────────────────────────
  const kiteConnected = kiteStatus?.connected;

  const kiteStale = (() => {
    if (!kiteConnected || !kiteStatus?.last_tick_at) return false;
    try {
      const tickAge = (Date.now() - new Date(kiteStatus.last_tick_at).getTime()) / 1000;
      const nowIST  = new Date().toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' });
      const [h, m]  = nowIST.split(':').map(Number);
      const inMktHrs = (h > 9 || (h === 9 && m >= 15)) && (h < 15 || (h === 15 && m <= 30));
      return inMktHrs && tickAge > 60;
    } catch { return false; }
  })();

  // Each issue: { id, label, severity: 'critical'|'caution', action? }
  const issues = [];
  if (!backendUp) {
    issues.push({ id: 'backend-offline', label: 'Backend offline', severity: 'critical',
      inlineAction: { label: 'Retry', onClick: () => getHealth().then(r => setBackendUp(!r._error)).catch(() => {}) } });
  }
  if (!kiteConnected) {
    issues.push({ id: 'kite-login', label: 'Login to Kite', severity: 'critical',
      inlineAction: { label: 'Login', href: KITE_LOGIN_URL } });
  } else if (deskState === 'TICKER_DOWN') {
    // Token valid but WebSocket dropped in steady state — re-login forces restart.
    issues.push({ id: 'ticker-down', label: 'Desk ticker down', severity: 'critical',
      inlineAction: { label: 'Re-login', href: KITE_LOGIN_URL } });
  } else if (deskState === 'TOKEN_EXPIRED') {
    issues.push({ id: 'token-expired', label: 'Kite token expired', severity: 'critical',
      inlineAction: { label: 'Login', href: KITE_LOGIN_URL } });
  } else if (kiteStale) {
    issues.push({ id: 'prices-stale', label: 'Prices stale', severity: 'caution',
      inlineAction: { label: 'Refresh', onClick: async () => { setRefreshing(true); try { await apiPost('/api/prices/refresh-prev-close'); await refreshPrices(); } catch {} setRefreshing(false); } } });
  }
  if (indicesStale) {
    issues.push({ id: 'quote-feed-stale', label: 'Quote feed stale', severity: 'caution',
      inlineAction: { label: 'Retry', onClick: async () => { setRefreshing(true); try { await refreshPrices(); } catch {} setRefreshing(false); } } });
  }

  const worst      = issues[0] || null;
  const extraCount = issues.length - 1;

  // Panel severity label
  const panelSeverity = issues.length === 0
    ? { label: 'All systems healthy', icon: '✦' }
    : issues.some(i => i.severity === 'critical')
      ? { label: 'Action required', icon: '⚠' }
      : { label: 'Needs attention', icon: '⚙' };

  // ── Format helpers ──
  const fmtHHMM = ts => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
      });
    } catch { return '—'; }
  };

  const eodLabel = (() => {
    const d = dataStatus?.data_as_of;
    if (!d) return '—';
    try {
      return new Date(d + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch { return d; }
  })();

  const fiiCashStatus = (() => {
    const latest = dataStatus?.fii_cash_latest_date;
    if (!latest) return { label: 'No data', amber: true };
    // Last trading day = most recent weekday before today
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    while (yesterday.getDay() === 0 || yesterday.getDay() === 6) {
      yesterday.setDate(yesterday.getDate() - 1);
    }
    const lastTradingStr = yesterday.toISOString().slice(0, 10);
    const stale = latest < lastTradingStr;
    const dateLabel = new Date(latest + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return stale
      ? { label: `${dateLabel} · stale`, amber: true }
      : { label: dateLabel, amber: false };
  })();

  const kiteTimeLabel = (() => {
    const src = indicesRaw?.as_of ? new Date(indicesRaw.as_of) : lastFetched;
    if (!src) return '—';
    try {
      return src.toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
      });
    } catch { return '—'; }
  })();

  const kiteLastLinked = fmtHHMM(kiteStatus?.login_at);
  const kiteLastSeen   = fmtHHMM(kiteStatus?.last_tick_at || kiteStatus?.login_at);

  // ── Popover action button ──
  const PanelBtn = ({ onClick, href, disabled, busy, label, busyLabel }) => {
    const style = {
      fontSize: 11, fontWeight: 600, padding: '3px 10px',
      borderRadius: 'var(--r-1)', border: '0.5px solid var(--hair-strong)',
      background: 'var(--surface-2)',
      color: (disabled || busy) ? 'var(--ink-3)' : 'var(--ink-2)',
      cursor: (disabled || busy) ? 'default' : 'pointer',
      opacity: (disabled || busy) ? 0.6 : 1,
      fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap',
      display: 'flex', alignItems: 'center', gap: 5,
      textDecoration: 'none',
    };
    const content = busy ? busyLabel : label;
    if (href) return <a href={href} target="_blank" rel="noreferrer" style={style}>{content}</a>;
    return <button onClick={onClick} disabled={disabled || busy} style={style}>{content}</button>;
  };

  // ── Panel section header ──
  const SectionHead = ({ children }) => (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
      color: 'var(--ink-3)', padding: '10px 0 5px',
      borderTop: '0.5px solid var(--hair)',
    }}>
      {children}
    </div>
  );

  // ── Panel row ──
  const PanelRow = ({ Icon, label, status, statusAmber, action }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
      <span style={{ flexShrink: 0, color: 'var(--ink-3)', display: 'flex', alignItems: 'center' }}>
        <Icon size={13} strokeWidth={1.75} />
      </span>
      <span style={{ fontSize: 12, color: 'var(--ink-2)', fontFamily: 'var(--font-ui)', width: 82, flexShrink: 0 }}>
        {label}
      </span>
      <span style={{
        flex: 1, fontSize: 12, fontFamily: 'var(--font-data)', whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
        color: statusAmber ? 'var(--amber-ink)' : 'var(--ink-2)',
      }}>
        {status}
      </span>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );

  // ── Handlers ──
  const handleRefreshPrices = async () => {
    setRefreshing(true);
    try { await apiPost('/api/prices/refresh-prev-close'); await refreshPrices(); } catch {}
    setRefreshing(false);
  };
  const handleSyncEod = async () => {
    if (syncBusy) return;
    setSyncBusy(true);
    try { await syncEodData(); setTimeout(() => setSyncBusy(false), 15_000); }
    catch { setSyncBusy(false); }
  };
  const handleRecomputeRS = async () => {
    if (recomputeBusy) return;
    setRecomputeBusy(true);
    try { await recomputeRS(); setTimeout(() => setRecomputeBusy(false), 30_000); }
    catch { setRecomputeBusy(false); }
  };
  const handleRefreshAll = async () => {
    handleRefreshPrices();
    if (isOwner) { handleSyncEod(); handleRecomputeRS(); }
  };

  // ── Chip ──
  const renderChip = () => {
    // Healthy: green dot only, opens panel on click
    if (!worst) {
      return (
        <button
          onClick={() => setOpen(o => !o)}
          title="All systems healthy"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: '50%',
            background: 'transparent', border: 'none', cursor: 'pointer',
            position: 'relative',
          }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#4a9e6e',
            display: 'inline-block',
            boxShadow: '0 0 0 2px rgba(74,158,110,0.18)',
          }}/>
        </button>
      );
    }

    // Degraded: amber pill — worst label + inline action btn + +N badge + caret
    const isAmberBg = open;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {/* Label side — opens panel */}
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 8px 4px 10px',
            background: 'var(--amber-bg)',
            border: '0.5px solid var(--amber-hair)',
            borderRight: 'none',
            borderRadius: 'var(--r-2) 0 0 var(--r-2)',
            cursor: 'pointer', color: 'var(--amber-ink)',
            fontSize: 11, fontFamily: 'var(--font-ui)', fontWeight: 600,
          }}
        >
          {worst.label}
          {extraCount > 0 && (
            <span style={{
              background: 'var(--amber-ink)', color: '#fff',
              fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
            }}>
              +{extraCount}
            </span>
          )}
          <span style={{ fontSize: 10, color: 'var(--amber-ink)', opacity: 0.7 }}>
            {open ? '▴' : '▾'}
          </span>
        </button>

        {/* Inline action button */}
        {worst.inlineAction && (
          worst.inlineAction.href
            ? <a
                href={worst.inlineAction.href}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px',
                  background: 'var(--amber-ink)', color: '#fff',
                  border: '0.5px solid var(--amber-ink)',
                  borderRadius: '0 var(--r-2) var(--r-2) 0',
                  fontSize: 11, fontFamily: 'var(--font-ui)', fontWeight: 600,
                  cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 12 }}>→</span>
                {worst.inlineAction.label}
              </a>
            : <button
                onClick={worst.inlineAction.onClick}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px',
                  background: 'var(--amber-ink)', color: '#fff',
                  border: '0.5px solid var(--amber-ink)',
                  borderRadius: '0 var(--r-2) var(--r-2) 0',
                  fontSize: 11, fontFamily: 'var(--font-ui)', fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 12 }}>↺</span>
                {worst.inlineAction.label}
              </button>
        )}
      </div>
    );
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {renderChip()}

      {open && (
        <div style={{
          position: 'fixed', top: pos.top, right: pos.right,
          zIndex: 9999,
          background: 'var(--surface)',
          border: '0.5px solid var(--hair)',
          borderRadius: 'var(--r-3)', boxShadow: 'var(--shadow-panel)',
          width: 340, padding: '12px 16px 14px',
        }}>
          {/* Panel header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{
              fontSize: 13, lineHeight: 1,
              color: issues.length === 0 ? '#4a9e6e' : 'var(--amber-ink)',
            }}>
              {panelSeverity.icon}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 700, color: 'var(--ink)',
              fontFamily: 'var(--font-ui)', flex: 1,
            }}>
              {panelSeverity.label}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-data)' }}>
              as of {panelSec}
            </span>
          </div>

          {/* CONNECTION */}
          <SectionHead>Connection</SectionHead>

          <PanelRow
            Icon={Server}
            label="Backend"
            status={backendUp
              ? 'Online'
              : `Offline · last seen ${backendSeen ? fmtHHMM(backendSeen) : '—'}`}
            statusAmber={!backendUp}
            action={
              <PanelBtn
                label={backendUp ? 'Reconnect' : 'Retry'}
                onClick={() => getHealth().then(r => setBackendUp(!r._error)).catch(() => {})}
              />
            }
          />

          <PanelRow
            Icon={Link2}
            label="Kite"
            status={
              kiteConnected
                ? `Linked · ${kiteLastLinked}`
                : kiteLastLinked !== '—'
                  ? `Expired · last linked ${kiteLastLinked}`
                  : 'Session expired'
            }
            statusAmber={!kiteConnected}
            action={
              <PanelBtn
                label={kiteConnected ? 'Re-login' : 'Login'}
                href={KITE_LOGIN_URL}
              />
            }
          />

          {deskState && deskState !== 'NOT_CONNECTED' && (
            <PanelRow
              Icon={Radio}
              label="Desk ticker"
              status={
                deskState === 'TICKER_DOWN'  ? 'Down · re-login to restart' :
                deskState === 'TOKEN_EXPIRED'? 'Token expired' :
                deskState === 'LIVE'         ? 'Live' :
                deskState === 'STALE'        ? 'Stale' :
                deskState === 'MARKET_CLOSED'? 'Market closed' :
                deskState === 'DISCONNECTED' ? 'Disconnected' :
                deskState === 'AWAITING_FIRST_TICK' ? 'Awaiting tick' :
                deskState
              }
              statusAmber={deskState === 'TICKER_DOWN' || deskState === 'TOKEN_EXPIRED' || deskState === 'STALE'}
              action={
                (deskState === 'TICKER_DOWN' || deskState === 'TOKEN_EXPIRED') ? (
                  <PanelBtn label="Re-login" href={KITE_LOGIN_URL} />
                ) : null
              }
            />
          )}

          {/* DATA FRESHNESS */}
          <SectionHead>Data Freshness</SectionHead>

          <PanelRow
            Icon={Activity}
            label="Live prices"
            status={
              kiteStale
                ? `Stale · last tick ${kiteTimeLabel}`
                : `Fresh · ${kiteTimeLabel}`
            }
            statusAmber={kiteStale}
            action={
              <PanelBtn
                onClick={handleRefreshPrices}
                busy={refreshing}
                label="Refresh"
                busyLabel="…"
              />
            }
          />

          {isOwner && (
            <PanelRow
              Icon={Database}
              label="EOD data"
              status={eodLabel}
              action={
                <PanelBtn
                  onClick={handleSyncEod}
                  busy={syncBusy}
                  label="Sync"
                  busyLabel="Syncing…"
                />
              }
            />
          )}

          {isOwner && (
            <PanelRow
              Icon={BarChart2}
              label="RS rankings"
              status={fmtHHMM(dataStatus?.rs_computed_at)}
              action={
                <PanelBtn
                  onClick={handleRecomputeRS}
                  busy={recomputeBusy}
                  label="Recompute"
                  busyLabel="Computing…"
                />
              }
            />
          )}

          {isOwner && (
            <PanelRow
              Icon={Rss}
              label="News archive"
              status={fmtHHMM(dataStatus?.news_archiver_last_run)}
            />
          )}

          {isOwner && (
            <PanelRow
              Icon={TrendingUp}
              label="FII/DII cash"
              status={fiiCashStatus.label}
              statusAmber={fiiCashStatus.amber}
            />
          )}

          {/* Footer */}
          <div style={{
            borderTop: '0.5px solid var(--hair)', marginTop: 8, paddingTop: 8,
            fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.55,
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          }}>
            <span>Prices auto-refresh every 2 min · RS recomputes daily 16:05.</span>
            <button
              onClick={handleRefreshAll}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, color: 'var(--amber-ink)',
                padding: 0, fontFamily: 'var(--font-ui)', flexShrink: 0, marginLeft: 8,
              }}
            >
              Refresh all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Top bar ────────────────────────────────────────────────────────────────
export default function TopBar({ onMenuToggle, onLogoClick }) {
  return (
    <header className="app-topbar" style={{
      background: 'var(--paper)',
      borderBottom: '0.5px solid var(--hair)',
      position: 'sticky', top: 0, zIndex: 100,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 44, padding: '0 14px', gap: 10,
      }}>

        {/* ── Left: hamburger · logo ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>

          {/* Mobile hamburger */}
          <button
            className="sb-hamburger"
            onClick={onMenuToggle}
            title="Open navigation"
            style={{
              display: 'none',
              alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none',
              cursor: 'pointer', color: 'var(--ink-2)',
              padding: '2px 4px', borderRadius: 'var(--r-1)',
            }}
          >
            <Menu size={18} strokeWidth={1.75} />
          </button>

          {/* Mobile: mark only */}
          <button className="topbar-logo-mobile"
            onClick={onLogoClick}
            style={{ display: 'none', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <img src={logoMark} alt="terminal" width={20} height={20} style={{ display: 'block' }}/>
          </button>

          {/* Desktop: full lockup SVG — text vertically centred with mark */}
          <button className="topbar-logo"
            onClick={onLogoClick}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
            <img
              src={logoLockup}
              alt="terminal"
              style={{ height: 26, width: 'auto', display: 'block' }}
            />
          </button>
        </div>

        {/* ── Right: Search · Today's Actions · Clock · Status ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GlobalSearchBar />
          <TodayActions />
          {/* Thin separator before clock */}
          <div style={{ width: '0.5px', height: 20, background: 'var(--hair)', flexShrink: 0, margin: '0 2px' }} />
          <ClockDate />
          <StatusChip />
        </div>

      </div>
    </header>
  );
}
